/**
 * GuidedStart — after the officer picks the demo (or case) path: "Start this case
 * from…" → pre-wired board. Sample hints only appear on the training path.
 */
import { GUIDED_STARTS, evidenceById, type InputField } from '../../../case-canvas/registry';
import { useIntakeStore, newIntakeId, type IntakeCard } from '../../../stores/useIntakeStore';
import { GUIDED_SAMPLE_HINTS, type GuidedSampleId } from '../../../data/fusionIntakeSamples';

function defaultInputs(fields?: InputField[]): Record<string, unknown> {
  const v: Record<string, unknown> = {};
  (fields || []).forEach((f) => { if (f.kind === 'toggle') v[f.key] = !!f.default; else if (f.kind === 'number') v[f.key] = f.default; });
  return v;
}

export function GuidedStart({ training = false }: { training?: boolean } = {}) {
  const startFrom = useIntakeStore((s) => s.startFrom);

  const pick = (id: string, cardIds: string[]) => {
    const cards: IntakeCard[] = cardIds.map((rid) => {
      const ev = evidenceById(rid);
      return { id: newIntakeId(), registryId: rid, inputs: defaultInputs(ev?.inputs), status: 'idle' };
    });
    startFrom(id, cards);
  };

  return (
    <div className="h-full grid place-items-center p-6">
      <div className="max-w-2xl w-full">
        <h2 className="text-lg font-semibold text-center">
          {training ? 'Start a training board from…' : 'Start this case from…'}
        </h2>
        <p className="text-xs text-muted-foreground text-center mt-1 mb-5">
          {training
            ? 'These templates use sample / synthetic data on the demo sandbox — separate from live LE cases.'
            : 'Pick where you are starting — we open a pre-wired board with the first card ready.'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {GUIDED_STARTS.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => pick(g.id, g.cards)}
              data-testid={`guided-start-${g.id}`}
              className="flex items-start gap-3 text-left rounded-lg border border-border bg-card px-4 py-3 hover:border-primary hover:bg-secondary/40 transition-colors"
            >
              <g.icon size={20} className="text-primary mt-0.5 shrink-0" />
              <span>
                <span className="block text-sm font-medium">{g.label}</span>
                <span className="block text-[11px] text-muted-foreground mt-0.5">{g.sub}</span>
                {training && GUIDED_SAMPLE_HINTS[g.id as GuidedSampleId] && (
                  <span className="block text-[10px] font-medium text-foreground/70 mt-1.5">{GUIDED_SAMPLE_HINTS[g.id as GuidedSampleId]}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
